import {
  Box,
  Button,
  Flex,
  GridItem,
  Heading,
  Icon,
  Spacer,
  Text,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FaClock, FaCopy, FaEdit } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  ContractStatusDto,
  ContractTemplateMinDto,
  CounterpartyDto,
  useCopyContractMutation,
} from "../../../autogen/bff-api";
import { isApiErrorDto } from "../../../common/errors/isApiErrorDto";
import { isFetchBaseQueryError } from "../../../common/errors/isFetchBaseQueryError";
import { isForbiddenError } from "../../../common/errors/isNoAccessError";
import { useApiError } from "../../../common/errors/useApiError";
import { useDisplayDate } from "../../../common/formatting/useDisplayDate";
import { useSub } from "../../../common/subscription/useSub";
import { urls } from "../../../urls";
import { ProfileImageRounded } from "../../../common/persons/ProfileImageRounded";
import { ContractStatusTag } from "../edit/ContractStatusTag";
import { ContractTemplateTag } from "../edit/ContractTemplateTag";
import { getCounterpartyName } from "../view-all/ContractRow";

interface Props {
  id: string;
  title: string;
  counterparty: CounterpartyDto | undefined;
  status?: ContractStatusDto;
  expirationDate: string | null;
  canEdit: boolean;
  canCopy: boolean;
  template: ContractTemplateMinDto | null;
}

const COPY_REFUSED_TOAST = "contract-copy-refused";

export const ContractMeta = ({
  id,
  title,
  counterparty,
  status,
  expirationDate,
  canEdit,
  canCopy,
  template,
}: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const displayer = useApiError();
  const toast = useToast();
  const [copyContract, { isLoading: isCopyingContract }] =
    useCopyContractMutation();
  const { hasContractAccess } = useSub();

  const showCopyRefusal = (description: string) => {
    if (toast.isActive(COPY_REFUSED_TOAST)) return;
    toast({ id: COPY_REFUSED_TOAST, description, status: "error" });
  };

  const counterpartyName = counterparty
    ? getCounterpartyName(counterparty)
    : undefined;

  return (
    <>
      <GridItem
        area={"title"}
        display="flex"
        alignItems={"end"}
        flexWrap={{ base: "wrap", md: "nowrap" }}
        rowGap="2"
        minW="0"
        pt="10px"
        data-testid="contract-title-row"
      >
        <Heading as="h2" size="md" overflowWrap="anywhere">
          {title}
        </Heading>
        <Box pl="2.5">{status && <ContractStatusTag status={status} />}</Box>
        {template && (
          <Box ml="10px">
            <ContractTemplateTag template={template} />
          </Box>
        )}
        <Spacer />
      </GridItem>
      {canEdit && (
        <GridItem
          area="edit"
          display={"flex"}
          justifyContent={{ base: "start", md: "end" }}
          paddingTop={{ base: "2", md: "24px" }}
          gap="2"
          data-testid="contract-header-actions"
        >
          {canCopy && (
            <Tooltip
              label={
                hasContractAccess
                  ? t(
                      "Creates a new contract with a copy of documents and data fields",
                    )
                  : t("NotInPlan")
              }
              shouldWrapChildren={!hasContractAccess}
            >
              <Button
                leftIcon={<Icon as={FaCopy} w="15px" h="15px" mt="-2px" />}
                variant={"ghost"}
                colorScheme="teal"
                size={"sm"}
                isLoading={isCopyingContract}
                isDisabled={!hasContractAccess}
                onClick={async () => {
                  try {
                    const copy = await copyContract({
                      contractId: id,
                    }).unwrap();
                    navigate(urls.contracts.edit.go(copy.id).details);
                  } catch (e) {
                    if (
                      isFetchBaseQueryError(e) &&
                      isApiErrorDto(e.data) &&
                      e.data.errorCode
                    ) {
                      showCopyRefusal(t(e.data.errorCode));
                    } else if (isForbiddenError(e)) {
                      showCopyRefusal(
                        t(
                          "You don't have access to this contract. Ask an administrator in your organization for access.",
                        ),
                      );
                    } else {
                      displayer.triggerSoft(e);
                    }
                  }
                }}
              >
                {t("Copy")}
              </Button>
            </Tooltip>
          )}
          <Button
            leftIcon={<Icon as={FaEdit} w="15px" h="15px" mt="-2px" />}
            variant={"outline"}
            colorScheme="teal"
            size={"sm"}
            onClick={() => navigate(urls.contracts.edit.go(id).details)}
          >
            {t("Edit")}
          </Button>
        </GridItem>
      )}
      <GridItem area={"createdMeta"}>
        {counterpartyName && (
          <Flex mt="9px">
            <Flex>
              <ProfileImageRounded
                name={counterpartyName}
                width="20px"
                backgroundColor="blue.500"
                fontSize={"xx-small"}
              />
            </Flex>
            <Flex justifyContent={"center"} mt="-3px" ml="5px">
              <Text fontSize={"lg"}>{counterpartyName}</Text>
            </Flex>
          </Flex>
        )}
      </GridItem>
      <GridItem
        area={"deadline"}
        borderBottom="1px solid"
        borderColor="smBorder"
        display={"flex"}
        alignItems="center"
        paddingBottom="20px"
        paddingTop="3px"
      >
        {expirationDate && (
          <Flex alignItems="center" pt="5">
            <Flex>
              <Icon
                as={FaClock}
                color="teal"
                _dark={{ color: "teal.200" }}
                w="14px"
                h="14px"
              />
            </Flex>
            <Flex ml="5px">
              <Text fontSize={"md"} color="teal" _dark={{ color: "teal.200" }}>
                {t("Expiry date")}: {displayDate(expirationDate)}
              </Text>
            </Flex>
            <Spacer />
          </Flex>
        )}
      </GridItem>
    </>
  );
};
